import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import { format } from "date-fns";

import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { Edit, Circle } from "@mui/icons-material";

import {
  CalendarIcon,
  DetailsIcon,
  RatingIcon,
  TrendUpIcon,
  WalletIcon,
} from "../../components/icons";

import {
  PilotActivity
} from "./tabs/Activity";
import {
  PilotAssignments
} from "./tabs/Assignments";
import {
  PilotPayments
} from "./tabs/Payments";
import {
  PilotReviews
} from "./tabs/Reviews";
import PilotOverview from "./tabs/Overview";
import Availability from "./tabs/Availability";
import EditCrewDialog from "./EditCrew";
import { PilotAssignmentDialog } from "./components";
import { PilotStatistics } from "./components";
import apiService from "../../service/Api";
import { RootState } from "../../store/Index";

import { MessageIcon, StreamsIcon } from "../../components/icons";
import { PilotAPIResponse, PilotProps } from "./types/view-pilot";
import { EditAvatar } from "./components/EditProfilePicture";
import { AxiosError } from "axios";
import { toast } from "react-toastify";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

const ViewCrewMember: React.FC = () => {
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isPilotAssignmentDialogOpen, setIsPilotAssignmentDialogOpen] =
    useState(false);

  const reduxTheme = useSelector((state: RootState) => state.theme);

  const handleEditClick = () => {
    setIsEditDialogOpen(true);
  };
  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false);
  };

  const handlePilotAssignmentClick = () => {
    setIsPilotAssignmentDialogOpen(true);
  };
  const handleClosePilotAssignmentDialog = () => {
    setIsPilotAssignmentDialogOpen(false);
  };

  const [tabValue, setTabValue] = useState(0);
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  const { id } = useParams<{ id: string }>();
  const [member, setMember] = useState<PilotProps | null>(null);

  const fetchMember = useCallback(async () => {
    try {
      const pilot = await apiService.get<PilotAPIResponse>(
        `/api/crews/crew-members/${id}/`,
      );

      setMember({
        id: pilot.data.id,
        user_id: pilot.data.user.id,
        average_rating: pilot.data.average_rating,
        rating_count: pilot.data.rating_count,
        pilot_type: pilot.data.user.user_type,
        name: pilot.data.user.first_name || "",
        surname: pilot.data.user.last_name || "",
        email: pilot.data.user.email || "",
        username: pilot.data.user.username || "",
        phone: pilot.data.user?.phone_number || "",
        organization: pilot.data.user?.organization || "",
        date_of_birth: pilot.data.user.date_of_birth || "",
        role: pilot.data.user.role,
        department: pilot.data.roles_detail[0]?.name || "",
        status: "Available", // Need more logic for this
        avatar: pilot.data.user.profile_picture || "",
        joinDate: format(new Date(pilot.data.created_at), "dd MMM, yyyy"),
        totalEarnings: 0, // Not available in response, calculate these on the fly
        totalAssignments: 0, // Not available in response, calculate these on the fly
        averageRating: 0, // Not available in response, calculate these on the fly
        lastGig: "N/A", // Not available in response, calculate these on the fly
        nextGig: "N/A", // Not available in response, calculate these on the fly
        roles: pilot.data.roles,
        skills: pilot.data.skills,
        tools: pilot.data.tools,
        rolesDetail: pilot.data.roles_detail || [],
        skillsDetail: pilot.data.skills_detail || [],
        toolsDetail: pilot.data.tools_detail || [],
        documents: pilot.data.document_certifications || [], // Not available in response
        emergencyContact: pilot.data.user.emergency_contact_phone || "",
        emergencyContactName: pilot.data.user.emergency_contact || "",
        street_address: pilot.data.user.street_address || "",
        maritalStatus: pilot.data.user.marital_status || "",
        occupation: pilot.data.user.occupation || "",
        nationalId: pilot.data.user.national_id || "",
        city: pilot.data.user.city || "",
        state: pilot.data.user.state_province || "",
        postal_code: pilot.data.user.postal_code || "",
        country: pilot.data.user.residence_country || "",
        preferences: pilot.data.user.service_preferences || "",
        bio: pilot.data.bio || "",
        experience_years: pilot.data.experience_years || 0,
      });
    } catch (error) {
      if (error instanceof AxiosError) {
        const errorMessage =
          error.response?.data?.message ||
          "Failed to load crew member. Please try again.";
        toast.error(errorMessage);
      } else {
        console.error("Failed to fetch member", error);
      }
    } finally {
      // no-op
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchMember();
    }
  }, [fetchMember, id]);

  if (!id || !member) return null;

  return (
    <motion.div
      initial={{ opacity: 0, x: 300 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 300 }}
      transition={{ duration: 0.3 }}
    >
      <Box sx={{ minHeight: "100vh", position: "relative" }}>
        {/* Pilot Banner */}
        <Card
          sx={{
            mb: "16px",
            boxShadow: 2,
            height: "100%",
            position: "sticky",
            transition: "1s ease-in-out",
            zIndex: 9,
            top: -16,
          }}
        >
          <CardContent>
            <Grid container>
              <Grid size={{ lg: 1 }}>
                <EditAvatar
                  fetchMember={fetchMember}
                  initialImage={member?.avatar}
                  pilot={{
                    id: member?.user_id,
                    email: member?.email,
                    name: member?.name,
                  }}
                />
              </Grid>
              <Grid size={{ lg: 11 }}>
                <Box
                  display={"flex"}
                  alignItems={"center"}
                  justifyContent={"space-between"}
                >
                  <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                    {member?.name} {member?.surname}
                  </Typography>
                  <Box
                    display={"flex"}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                    gap={"8px"}
                  >
                    <Button
                      variant="outlined"
                      onClick={handlePilotAssignmentClick}
                      startIcon={
                        <StreamsIcon color="inherit" sx={{ fontSize: 20 }} />
                      }
                      sx={{
                        borderRadius: 1.5,
                        textTransform: "none",
                      }}
                    >
                      Assign to Event
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={
                        <MessageIcon color="inherit" sx={{ fontSize: 20 }} />
                      }
                      sx={{
                        borderRadius: 1.5,
                        textTransform: "none",
                      }}
                    >
                      Message
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<Edit />}
                      onClick={handleEditClick}
                      sx={{
                        backgroundColor: reduxTheme.primary_colors[400],
                        borderRadius: 1.5,
                        textTransform: "none",
                      }}
                    >
                      Edit
                    </Button>
                  </Box>
                </Box>
                <Chip
                  label={"Active"}
                  icon={<Circle color={"success"} sx={{ width: "8px" }} />}
                  size="small"
                  variant="outlined"
                  sx={{
                    borderRadius: 1.5,
                    px: 0.5,
                    borderColor: "#4BA66340",
                    bgcolor: "#4BA66340",
                    color: "#4BA663",
                  }}
                />
                <Box mt={1} display={"flex"} alignItems={"center"} gap={0.4}>
                  {member.rolesDetail?.length !== 0 &&
                    member.rolesDetail.map((role) => {
                      return (
                        <Chip
                          label={role.name}
                          key={role.id}
                          size="small"
                          variant="outlined"
                          sx={{
                            borderRadius: 1.5,
                            borderColor: reduxTheme.primary_colors[800],
                          }}
                        />
                      );
                    })}
                </Box>
                <Box
                  sx={{ mt: "8px" }}
                  display={"flex"}
                  alignItems={"center"}
                  gap={"4px"}
                >
                  <CalendarIcon sx={{ fontSize: 20 }} color="disabled" />
                  <Typography
                    variant={"body2"}
                  >{`Joined ${member?.joinDate}`}</Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Stats Cards */}
        <PilotStatistics pilot={member} />

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            variant={"fullWidth"}
            value={tabValue}
            onChange={handleTabChange}
          >
            <Tab
              label={
                <Box display={"flex"} alignItems={"center"} gap={1}>
                  <DetailsIcon color={"disabled"} sx={{ fontSize: 20 }} />
                  <Typography variant={"body2"}>Overview</Typography>
                </Box>
              }
            />
            <Tab
              label={
                <Box display={"flex"} alignItems={"center"} gap={1}>
                  <StreamsIcon color={"disabled"} sx={{ fontSize: 20 }} />
                  <Typography variant={"body2"}>Assignments</Typography>
                </Box>
              }
            />
            <Tab
              label={
                <Box display={"flex"} alignItems={"center"} gap={1}>
                  <RatingIcon color={"disabled"} sx={{ fontSize: 22 }} />
                  <Typography variant={"body2"}>Reviews</Typography>
                </Box>
              }
            />
            <Tab
              label={
                <Box display={"flex"} alignItems={"center"} gap={1}>
                  <WalletIcon color={"disabled"} sx={{ fontSize: 20 }} />
                  <Typography variant={"body2"}>Payments</Typography>
                </Box>
              }
            />
            <Tab
              label={
                <Box display={"flex"} alignItems={"center"} gap={1}>
                  <CalendarIcon color={"disabled"} sx={{ fontSize: 20 }} />
                  <Typography variant={"body2"}>Availability</Typography>
                </Box>
              }
            />
            <Tab
              label={
                <Box display={"flex"} alignItems={"center"} gap={1}>
                  <TrendUpIcon color={"disabled"} sx={{ fontSize: 22 }} />
                  <Typography variant={"body2"}>Activity</Typography>
                </Box>
              }
            />
          </Tabs>
        </Box>

        <AnimatePresence mode="wait">
          <TabPanel value={tabValue} index={0}>
            <PilotOverview pilot={member} />
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            <PilotAssignments pilot={member} />
          </TabPanel>
          <TabPanel value={tabValue} index={2}>
            <PilotReviews pilot={member} />
          </TabPanel>
          <TabPanel value={tabValue} index={3}>
            <PilotPayments pilot={member} />
          </TabPanel>
          <TabPanel value={tabValue} index={4}>
            <Availability pilot={member} />
          </TabPanel>
          <TabPanel value={tabValue} index={5}>
            <PilotActivity pilot={member} />
          </TabPanel>
        </AnimatePresence>

        {/* DIALOGS */}
        {member && (
          <>
            <EditCrewDialog
              open={isEditDialogOpen}
              onClose={handleCloseEditDialog}
              pilot={member}
            />

            <PilotAssignmentDialog
              open={isPilotAssignmentDialogOpen}
              onClose={handleClosePilotAssignmentDialog}
              pilot={member}
            />
          </>
        )}
      </Box>
    </motion.div>
  );
};

export default ViewCrewMember;
